import APIService from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import { FormatCurrency, userAvatar } from "@/common/helpers"
import ActionBar from "@/components/core/ActionBar"
import Pagination from "@/components/core/Pagination"
import { Routes } from "@/routes"
import { AgentSummaryItem } from "@/types/Agents.types"
import moment from "moment"
import { useCallback, useEffect, useState } from "react"
import { Badge, Card, Col, Container, Form, Row, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"

const QUANTITY_PER_PAGE = 40

const VarianceBadge = ({ value }: { value?: number | null }) => {
    if (value === null || value === undefined) return <span className="text-muted">—</span>

    const isUp = value >= 0
    return (
        <span className={`small fw-semibold ${isUp ? "text-success" : "text-danger"}`}>
            <i className={`fa fa-arrow-${isUp ? "up" : "down"} me-1`}></i>
            {Math.abs(value)}%
        </span>
    )
}

const AgentsPage = () => {
    const { t } = useTranslation()
    const history = useHistory()
    const user = parsedUser()

    const [isLoading, setIsLoading] = useState(false)
    const [records, setRecords] = useState<AgentSummaryItem[]>([])
    const [search, setSearch] = useState("")
    const [page, setPage] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [totalRecords, setTotalRecords] = useState(0)

    const bindData = useCallback(async (currentPage: number, currentSearch: string) => {
        if (!user?.OrganizationId) return

        try {
            setIsLoading(true)
            const request = await APIService.getAction(
                "agents/Summary",
                `OrganizationId=${user.OrganizationId}&Search=${encodeURIComponent(currentSearch)}&Page=${currentPage}&Quantity=${QUANTITY_PER_PAGE}`
            )

            if (request.data.status === "ok") {
                setRecords(request.data.response || [])
                setTotalPages(request.data.quantityOfPages ?? 0)
                setTotalRecords(request.data.quantityOfRecords ?? 0)
            }
        } catch (ex) {
            console.error(ex)
        } finally {
            setIsLoading(false)
        }
    }, [user?.OrganizationId])

    useEffect(() => {
        bindData(page, search)
    }, [page])

    const onSearch = (e: React.FormEvent) => {
        e.preventDefault()
        setPage(0)
        bindData(0, search)
    }

    // Portfolio totals, so the header answers "how big is the operation" at a glance
    const totals = records.reduce(
        (acc, item) => ({
            places: acc.places + item.assignedPlaces,
            customers: acc.customers + item.portfolioCustomers,
            sales: acc.sales + item.salesYtd,
        }),
        { places: 0, customers: 0, sales: 0 }
    )

    return (
        <Container fluid className="px-4 py-3">
            <ActionBar
                title={t("Agents")}
                icon="fa fa-users me-2"
                actions={
                    isLoading ? (
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    ) : null
                }
            />

            <Row className="g-2 align-items-end mt-1 mb-3">
                <Col xs={12} md={5} lg={4}>
                    <Form onSubmit={onSearch}>
                        <Form.Label className="small text-muted mb-1">{t("Search")}</Form.Label>
                        <div className="input-group input-group-sm">
                            <Form.Control
                                size="sm"
                                value={search}
                                placeholder={t("Name or email") as string}
                                onChange={e => setSearch(e.target.value)}
                            />
                            <button className="btn btn-sm btn-primary" type="submit">
                                <i className="fa fa-search"></i>
                            </button>
                        </div>
                    </Form>
                </Col>
                <Col xs={12} md={7} lg={8} className="text-md-end">
                    <span className="text-muted small me-3">
                        <i className="fa fa-users me-1"></i>
                        {totalRecords} {t("Agents")}
                    </span>
                    <span className="text-muted small me-3">
                        <i className="fa fa-map-marker-alt me-1"></i>
                        {totals.places} {t("Assigned Places")}
                    </span>
                    <span className="text-muted small me-3">
                        <i className="fa fa-address-book me-1"></i>
                        {totals.customers} {t("Customer Portfolio")}
                    </span>
                    <span className="text-muted small">
                        <i className="fa fa-dollar-sign me-1"></i>
                        {FormatCurrency(totals.sales)} {t("Sales YTD")}
                    </span>
                </Col>
            </Row>

            <Card className="shadow-sm border-0">
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table hover className="mb-0 align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th className="ps-3">{t("Agent")}</th>
                                    <th className="text-center">{t("Assigned Places")}</th>
                                    <th className="text-center">{t("Customer Portfolio")}</th>
                                    <th className="text-center">{t("Visits")}</th>
                                    <th>{t("Last Visit")}</th>
                                    <th className="text-end">{t("Sales YTD")}</th>
                                    <th className="text-end pe-3">{t("vs Previous Year")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {records.map(agent => (
                                    <tr
                                        key={agent.id}
                                        className="clickable"
                                        onClick={() => history.push(`${Routes.AgentDetails.clean}${agent.id}`)}
                                    >
                                        <td className="ps-3">
                                            <div className="d-flex align-items-center">
                                                <img
                                                    src={agent.pic || userAvatar(`${agent.firstName} ${agent.lastName}`, 32)}
                                                    alt={agent.firstName}
                                                    className="rounded-circle me-2"
                                                    style={{
                                                        width: 32,
                                                        height: 32,
                                                        objectFit: "cover",
                                                        border: agent.color ? `2px solid ${agent.color}` : undefined,
                                                    }}
                                                />
                                                <div>
                                                    <div className="fw-semibold">
                                                        {agent.firstName} {agent.lastName}
                                                    </div>
                                                    <small className="text-muted d-block">{agent.email}</small>
                                                    <div className="mt-1">
                                                        {agent.roleName && (
                                                            <Badge bg="primary" className="me-1">{agent.roleName}</Badge>
                                                        )}
                                                        {agent.externalSellerId ? (
                                                            <Badge bg="light" text="dark">
                                                                {t("Seller")} #{agent.externalSellerId}
                                                            </Badge>
                                                        ) : (
                                                            <Badge bg="light" text="muted">{t("No ERP link")}</Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <div className="fw-semibold">{agent.assignedPlaces}</div>
                                            <small className="text-muted">
                                                {agent.placesLinkedToErp} {t("in ERP")} · {agent.placesInSale} {t("in sale")}
                                            </small>
                                        </td>
                                        <td className="text-center">
                                            <div className="fw-semibold">{agent.portfolioCustomers}</div>
                                            <small className="text-muted">{t("ERP customers")}</small>
                                        </td>
                                        <td className="text-center">
                                            <div className="fw-semibold">{agent.totalVisits}</div>
                                            <small className="text-muted">
                                                {agent.visitsThisMonth} {t("this month")}
                                            </small>
                                        </td>
                                        <td>
                                            {agent.lastVisitDate ? (
                                                <>
                                                    <div className="small">{moment(agent.lastVisitDate).format("ll")}</div>
                                                    <small className="text-muted">{moment(agent.lastVisitDate).fromNow()}</small>
                                                </>
                                            ) : (
                                                <span className="text-muted">—</span>
                                            )}
                                        </td>
                                        <td className="text-end">
                                            <div className="fw-semibold">{FormatCurrency(agent.salesYtd)}</div>
                                            <small className="text-muted">
                                                {agent.invoicesYtd} {t("Invoices")}
                                            </small>
                                        </td>
                                        <td className="text-end pe-3">
                                            <VarianceBadge value={agent.salesVariancePercent} />
                                            <small className="text-muted d-block">
                                                {FormatCurrency(agent.salesPreviousYtd)}
                                            </small>
                                        </td>
                                    </tr>
                                ))}
                                {!isLoading && records.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="text-center text-muted py-4">
                                            {t("No records found")}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
                {totalPages > 1 && (
                    <Card.Footer className="bg-white">
                        <Pagination
                            totalPages={totalPages}
                            currentPage={page}
                            totalRecords={totalRecords}
                            onPageChanged={(newPage: number) => setPage(newPage)}
                        />
                    </Card.Footer>
                )}
            </Card>
        </Container>
    )
}

export default AgentsPage
